/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { useCallback, useEffect, useRef } from "react";
import type { FieldErrors, FieldValues } from "react-hook-form";

/**
 * Scrolls to the first field with a validation error after form submission.
 *
 * Works with react-hook-form by watching submitCount to detect new submit
 * attempts. Only scrolls once per failed submit, not on every render.
 *
 * Field matching strategy (in order):
 * 1. Element with `name` attribute matching the error field key
 * 2. Element with `id` attribute matching the error field key
 * 3. Label with `htmlFor` matching the error field key
 *
 * @param errors - The `formState.errors` object from react-hook-form
 * @param submitCount - The `formState.submitCount` from react-hook-form
 */
export function useScrollToError<T extends FieldValues>(
  errors: FieldErrors<T>,
  submitCount: number,
) {
  const prevSubmitCountRef = useRef(submitCount);

  const scrollToFirstError = useCallback(() => {
    const errorKeys = Object.keys(errors);
    if (errorKeys.length === 0) return;

    for (const key of errorKeys) {
      // Try name attribute first (react-hook-form registers fields by name)
      let element = document.querySelector<HTMLElement>(`[name="${key}"]`);

      // Try id attribute
      if (!element) {
        element = document.getElementById(key);
      }

      // Try label's htmlFor
      if (!element) {
        const label = document.querySelector<HTMLLabelElement>(
          `label[for="${key}"]`,
        );
        if (label) {
          element = label;
        }
      }

      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        // Focus the element if it's an input/select/textarea
        if (
          element instanceof HTMLInputElement ||
          element instanceof HTMLSelectElement ||
          element instanceof HTMLTextAreaElement
        ) {
          element.focus({ preventScroll: true });
        }
        return;
      }
    }

    // Fallback: scroll to the first visible error message element
    const errorMessage = document.querySelector<HTMLElement>(
      '[role="alert"], [data-form-error], .text-destructive',
    );
    if (errorMessage) {
      errorMessage.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [errors]);

  // Trigger scroll only when submitCount increments (new submit attempt)
  useEffect(() => {
    if (submitCount <= prevSubmitCountRef.current) return;

    prevSubmitCountRef.current = submitCount;

    const errorKeys = Object.keys(errors);
    if (errorKeys.length === 0) return;

    // Small delay to ensure DOM has updated with error messages
    const timer = setTimeout(scrollToFirstError, 100);
    return () => clearTimeout(timer);
  }, [submitCount, errors, scrollToFirstError]);

  return { scrollToFirstError };
}
